/**
 * Profanity filter — detects offensive/vulgar words in multiple languages (EN + HU).
 * Uses word boundary matching to minimize false positives.
 */

// Common English offensive words (abbreviated list — covers the most common slurs and profanity)
const EN_WORDS = [
  "fuck", "fucking", "fucked", "fucker", "fucks", "motherfucker",
  "shit", "shitty", "bullshit", "shitting",
  "ass", "asshole", "arse", "arsehole",
  "bitch", "bitches", "bitchy",
  "dick", "dickhead",
  "cunt", "cunts",
  "bastard", "bastards",
  "damn", "damned", "dammit",
  "cock", "cocksucker",
  "piss", "pissed", "pissing",
  "whore", "slut", "sluts",
  "nigger", "nigga", "faggot", "fag", "retard", "retarded",
  "wanker", "twat", "tosser",
  "crap", "crappy",
  "douche", "douchebag",
  "stfu", "gtfo", "wtf", "lmfao",
];

// Common Hungarian offensive words
const HU_WORDS = [
  "kurva", "kurváz", "kurvás", "kurvára", "kurvákat",
  "fasz", "faszom", "faszfej", "faszkalap", "faszt",
  "geci", "gecis", "gennyes",
  "szar", "szaros", "szarás", "szarházi", "szart",
  "baszik", "baszd", "baszás", "basszus", "kibaszott", "megbasz", "lebasz", "baszódj",
  "buzi", "buzis",
  "köcsög", "köcsögök",
  "segg", "seggfej", "segghülye", "segglyuk",
  "pina", "pinás", "pinázik",
  "picsa", "piCSa", "picsás", "picsába",
  "cigány", "cigó",
  "anyád", "anyádat", "anyádat",
  "lófasz", "lószar",
  "rohadj", "rohadt", "rohadék",
  "dögölj",
  "mocskos",
  "tetves",
  "ribanc",
  "cafka",
  "szuka",
  "hülye", "hülyés",
  "idióta",
  "barom",
  "állat",
  "mocsok",
  "csicska",
  "putri",
  "kurafi",
];

// Build a Set for fast lookup (lowercased)
const PROFANITY_SET = new Set([...EN_WORDS, ...HU_WORDS].map(w => w.toLowerCase()));

// Build regex for partial/embedded matching of the most severe words
const SEVERE_PATTERNS = [
  "f+u+c+k+", "s+h+i+t+", "c+u+n+t+", "n+i+g+g+",
  "k+u+r+v+", "f+a+s+z+", "g+e+c+i+", "b+a+s+z+", "p+i+c+s+",
].map(p => new RegExp(p, "i"));

/**
 * Check if text contains profanity.
 * @param {string} text - The text to check
 * @returns {boolean} true if offensive content detected
 */
export function containsProfanity(text) {
  if (!text || typeof text !== "string") return false;

  const normalized = text
    .toLowerCase()
    .replace(/[0-9@#$%^&*]/g, match => {
      // Leet speak substitutions
      const map = { "0": "o", "1": "i", "3": "e", "4": "a", "5": "s", "8": "b", "@": "a", "$": "s" };
      return map[match] || match;
    })
    .replace(/[.,!?;:'"()\-_]/g, " ");

  // Word-boundary check against the full set
  const words = normalized.split(/\s+/).filter(Boolean);
  for (const word of words) {
    if (PROFANITY_SET.has(word)) return true;
  }

  // Severe pattern matching (catches obfuscated variants like "fuuuck", "kuuurva")
  for (const pattern of SEVERE_PATTERNS) {
    if (pattern.test(normalized)) return true;
  }

  return false;
}

// The banlist key is the shared package's `normalizeName` — the same copy the
// setDisplayName callable checks `bannedNames/{key}` with. Re-exported so the
// name-field consumers keep one import path for moderation helpers.
export { normalizeName } from "@sendly/shared";

// Maps a server-side moderation `category` (from the setDisplayName /
// submitRating callables, surfaced in HttpsError details) to an i18n key for a
// localized, category-specific rejection message. Context-neutral wording so
// the same set serves both the name field and the comment field. Unknown /
// "none" / "error" categories fall back to a generic message.
const MODERATION_CATEGORY_KEYS = {
  profanity: "moderation.cat.profanity",
  sexual: "moderation.cat.sexual",
  threat: "moderation.cat.threat",
  hate: "moderation.cat.hate",
  harassment: "moderation.cat.harassment",
  impersonation: "moderation.cat.impersonation",
};

export function moderationCategoryKey(category) {
  return MODERATION_CATEGORY_KEYS[category] || "moderation.cat.generic";
}
